import React, { useState, useRef } from 'react';
import Layout from '@/components/Layout';
import PremiumGate from '@/components/PremiumGate';
import { Camera, Upload, Loader, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// A realistic sample result shown (blurred) behind the paywall so free growers
// can see exactly what an AI diagnosis looks like before upgrading.
function SampleDiagnosis() {
  return (
    <div className="space-y-6" data-testid="scanner-sample-preview">
      <div className="bg-white rounded-3xl p-8 border border-[#1A4331]/5 shadow-sm">
        <div className="rounded-2xl overflow-hidden bg-[#F0F2EB] h-52 flex items-center justify-center">
          <img src="https://images.unsplash.com/photo-1592841200221-a6898f307baa?w=800&q=60" alt="Sample tomato leaf" className="w-full h-full object-cover" />
        </div>
      </div>
      <div className="bg-gradient-to-br from-[#8EAA97]/10 to-[#B4D330]/10 rounded-3xl p-8 border border-[#8EAA97]/20">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-[#1A4331] flex items-center justify-center">
            <AlertCircle className="w-6 h-6 text-[#B4D330]" />
          </div>
          <div>
            <h2 className="text-2xl font-semibold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>Early Blight detected</h2>
            <p className="text-sm text-[#5C665C]">Alternaria solani · High confidence</p>
          </div>
        </div>
        <div className="space-y-3 text-[#2D241E] text-sm leading-relaxed">
          <p><span className="font-semibold">What it is:</span> A common fungal disease showing as dark brown spots with concentric rings on lower, older leaves.</p>
          <p><span className="font-semibold">Treat it:</span> Remove affected leaves, improve airflow, water at the base, and apply a copper-based spray weekly.</p>
          <p><span className="font-semibold">Prevent it:</span> Mulch to stop soil splash, rotate crops next season, and avoid overhead watering.</p>
        </div>
      </div>
    </div>
  );
}

function PestScanner({ user }) {
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [description, setDescription] = useState('');
  const [diagnosis, setDiagnosis] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedImage(file);
      setPreviewUrl(URL.createObjectURL(file));
      setDiagnosis('');
    }
  };

  const handleAnalyze = async () => {
    if (!selectedImage) return;

    setIsAnalyzing(true);
    setDiagnosis('');

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target.result.split(',')[1];

        const response = await fetch(`${API}/ai/diagnose`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ 
            image: base64,
            description: description 
          })
        });

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let fullDiagnosis = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6);
              if (data === '[DONE]') break;
              let piece = data;
              try { piece = JSON.parse(data); } catch { /* raw fallback */ }
              fullDiagnosis += piece;
              setDiagnosis(fullDiagnosis);
            }
          }
        }
      };
      reader.readAsDataURL(selectedImage);
    } catch (error) {
      console.error('Error:', error);
      setDiagnosis('Sorry, I encountered an error analyzing the image. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <Layout user={user}>
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h1 className="text-4xl sm:text-5xl font-bold text-[#1A4331] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Pest & Disease Scanner
          </h1>
          <p className="text-base text-[#5C665C] mt-2 leading-relaxed">
            Upload a photo of your plant to identify pests, diseases, or deficiencies
          </p>
        </div>

        <PremiumGate user={user} preview={<SampleDiagnosis />} title="Pest & Disease Scanner — Premium" blurb="Snap a leaf and get an instant AI pest & disease diagnosis — like the sample behind this. Unlock with Homegrown Premium.">
        {/* Upload Area */}
        <div className="bg-white rounded-3xl p-8 border border-[#1A4331]/5 shadow-sm">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageSelect}
            className="hidden"
            data-testid="image-upload-input"
          />
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleImageSelect}
            className="hidden"
            data-testid="camera-capture-input"
          />

          {!previewUrl ? (
            <div
              data-testid="upload-area"
              className="border-2 border-dashed border-[#8EAA97] rounded-2xl p-10 sm:p-12 text-center"
            >
              <Camera className="w-16 h-16 text-[#8EAA97] mx-auto mb-4" />
              <p className="text-[#2D241E] font-semibold mb-1">Snap or upload a photo of the issue</p>
              <p className="text-sm text-[#5C665C] mb-6">Take a photo with your camera, or choose one from your device</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto">
                <button
                  onClick={() => cameraInputRef.current?.click()}
                  data-testid="take-photo-button"
                  className="flex-1 bg-[#1A4331] hover:bg-[#255C44] text-white font-semibold py-3 px-6 rounded-full transition-colors flex items-center justify-center gap-2"
                >
                  <Camera className="w-5 h-5" />
                  Take a photo
                </button>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="upload-photo-button"
                  className="flex-1 bg-white border border-[#8EAA97] text-[#1A4331] hover:bg-[#F0F2EB] font-semibold py-3 px-6 rounded-full transition-colors flex items-center justify-center gap-2"
                >
                  <Upload className="w-5 h-5" />
                  Upload photo
                </button>
              </div>
              <p className="text-xs text-[#5C665C] mt-4">PNG, JPG or WEBP (max 5MB)</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="relative rounded-2xl overflow-hidden">
                <img
                  src={previewUrl}
                  alt="Plant preview"
                  className="w-full h-auto max-h-96 object-contain bg-[#F0F2EB]"
                  data-testid="image-preview"
                />
              </div>
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => cameraInputRef.current?.click()}
                  data-testid="retake-photo-button"
                  className="text-[#1A4331] font-semibold hover:text-[#255C44] transition-colors flex items-center gap-2"
                >
                  <Camera className="w-4 h-4" />
                  Retake photo
                </button>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="change-image-button"
                  className="text-[#1A4331] font-semibold hover:text-[#255C44] transition-colors flex items-center gap-2"
                >
                  <Upload className="w-4 h-4" />
                  Choose another
                </button>
              </div>
            </div>
          )}

          {/* Optional Description */}
          {previewUrl && (
            <div className="mt-6">
              <label className="block text-sm font-semibold text-[#2D241E] mb-2">
                Additional context (optional)
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="e.g., yellowing leaves, spots appeared 2 days ago, only on lower leaves..."
                data-testid="description-input"
                className="w-full px-4 py-3 bg-[#F0F2EB] border-transparent focus:border-[#8EAA97] focus:ring-2 focus:ring-[#8EAA97]/20 rounded-xl outline-none transition-all resize-none"
                rows={3}
              />
            </div>
          )}

          {/* Analyze Button */}
          {previewUrl && (
            <button
              onClick={handleAnalyze}
              disabled={isAnalyzing}
              data-testid="analyze-button"
              className="mt-6 w-full bg-[#1A4331] hover:bg-[#255C44] text-white font-semibold py-4 px-8 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isAnalyzing ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Analyzing...
                </>
              ) : (
                'Analyze Plant Issue'
              )}
            </button>
          )}
        </div>

        {/* Diagnosis Result */}
        {diagnosis && (
          <div className="bg-gradient-to-br from-[#8EAA97]/10 to-[#B4D330]/10 rounded-3xl p-8 border border-[#8EAA97]/20" data-testid="diagnosis-result">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-[#1A4331] flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-[#B4D330]" />
              </div>
              <h2 className="text-2xl font-semibold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Diagnosis
              </h2>
            </div>
            <div className="prose prose-lg max-w-none">
              <p className="text-[#2D241E] whitespace-pre-wrap leading-relaxed">{diagnosis}</p>
            </div>
          </div>
        )}

        {/* Tips */}
        <div className="bg-white rounded-3xl p-6 border border-[#1A4331]/5 shadow-sm">
          <h3 className="font-semibold text-[#2D241E] mb-3">Tips for better diagnosis:</h3>
          <ul className="space-y-2 text-sm text-[#5C665C]">
            <li className="flex items-start gap-2">
              <span className="text-[#1A4331] mt-0.5">•</span>
              <span>Take clear, well-lit photos showing the affected area</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#1A4331] mt-0.5">•</span>
              <span>Include both close-ups and wider shots if possible</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#1A4331] mt-0.5">•</span>
              <span>Note when symptoms first appeared and any recent changes</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#1A4331] mt-0.5">•</span>
              <span>Photograph insects directly if visible</span>
            </li>
          </ul>
        </div>
        </PremiumGate>
      </div>
    </Layout>
  );
}

export default PestScanner;
